import { useState, useEffect } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle2,
  Loader2,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    zipCode: '',
    message: '',
    honeypot: '', // Hidden field for bots
    captchaAnswer: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0 });
  const [errors, setErrors] = useState({});

  // Generate simple math CAPTCHA
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2 });
  };

  const services = [
    { value: 'residential', label: language === 'es' ? 'Limpieza Residencial' : 'Residential Cleaning' },
    { value: 'commercial', label: language === 'es' ? 'Limpieza Comercial' : 'Commercial Cleaning' },
    { value: 'post-construction', label: language === 'es' ? 'Limpieza Post-Construcción' : 'Post-Construction Cleaning' },
    { value: 'move-in', label: language === 'es' ? 'Limpieza de Entrada' : 'Move In Cleaning' },
    { value: 'move-out', label: language === 'es' ? 'Limpieza de Salida' : 'Move Out Cleaning' },
    { value: 'deep-cleaning', label: language === 'es' ? 'Limpieza Profunda' : 'Deep Cleaning' },
    { value: 'recurring', label: language === 'es' ? 'Limpieza Recurrente' : 'Recurring Cleaning' },
    { value: 'emergency', label: language === 'es' ? 'Limpieza de Emergencia 🚨' : 'Emergency Cleaning 🚨' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.name.trim()) {
      newErrors.name = language === 'es' ? 'Nombre completo requerido' : 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = language === 'es' ? 'Correo electrónico requerido' : 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = language === 'es' ? 'Correo electrónico inválido' : 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = language === 'es' ? 'Número de teléfono requerido' : 'Phone number is required';
    } else if (!/^[\d\s\-\(\)\+]+$/.test(formData.phone) || formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = language === 'es' ? 'Número de teléfono inválido' : 'Invalid phone number';
    }

    if (!formData.service) {
      newErrors.service = language === 'es' ? 'Seleccione un servicio' : 'Please select a service';
    }

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = language === 'es' ? 'Código postal requerido' : 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
      newErrors.zipCode = language === 'es' ? 'Código postal inválido' : 'Invalid ZIP code';
    }

    // Check for links in message (spam protection)
    const linkPattern = /(https?:\/\/|www\.|\.com|\.net|\.org|\.io|<a\s|href=)/i;
    if (linkPattern.test(formData.message)) {
      newErrors.message = language === 'es' ? 'No se permiten enlaces en el mensaje' : 'Links are not allowed in the message';
    }

    // CAPTCHA validation
    const correctAnswer = captcha.num1 + captcha.num2;
    if (!formData.captchaAnswer || parseInt(formData.captchaAnswer) !== correctAnswer) {
      newErrors.captchaAnswer = language === 'es' ? 'Respuesta incorrecta' : 'Incorrect answer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if filled, it's a bot
    if (formData.honeypot) {
      console.log('Bot detected via honeypot');
      // Fake success to confuse bots
      setIsSubmitted(true);
      return;
    }

    if (!validateForm()) {
      toast.error(language === 'es' 
        ? 'Por favor corrija los errores en el formulario' 
        : 'Please fix the errors in the form'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Get service label for submission
      const selectedService = services.find(s => s.value === formData.service);
      
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: selectedService?.label || formData.service,
        zipCode: formData.zipCode,
        message: formData.message,
        captchaToken: 'verified' // Indicates captcha was solved
      });
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        zipCode: '',
        message: '',
        honeypot: '',
        captchaAnswer: ''
      });
      toast.success(language === 'es' 
        ? '¡Solicitud enviada! Nos comunicaremos pronto.' 
        : 'Request sent successfully! We\'ll contact you soon.'
      );
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.detail || 
        (language === 'es' 
          ? 'Error al enviar. Por favor intente de nuevo o llámenos.' 
          : 'Failed to send. Please try again or call us directly.');
      toast.error(errorMessage);
      // Regenerate captcha on error
      generateCaptcha();
      setFormData(prev => ({ ...prev, captchaAnswer: '' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-white py-12 md:py-20 watermark-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center animate-fadeInUp content-centered">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('contact.title')} <span className="text-[#1E88E5]">{t('contact.titleHighlight')}</span>
          </h1>
          <p className="text-lg text-[#334155] max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-surface py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Contact Form */}
            <div className="animate-fadeInUp">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#E2E8F0] h-full">
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('contact.formTitle')} <span className="text-[#4CAF50]">{t('contact.formHighlight')}</span>
                </h2>
                <p className="text-[#334155] text-sm mb-6">
                  {t('contact.formDescription')}
                </p>

                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="icon-container icon-container-green w-20 h-20 mx-auto mb-6">
                      <CheckCircle2 size={40} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('contact.thankYou')}
                    </h3>
                    <p className="text-[#334155] mb-6">
                      {t('contact.successMessage')}
                    </p>
                    <Button 
                      onClick={() => {
                        setIsSubmitted(false);
                        generateCaptcha();
                      }}
                      className="btn-primary"
                    >
                      {t('contact.sendAnother')}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot field - hidden from users, visible to bots */}
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        name="honeypot"
                        id="website"
                        value={formData.honeypot}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        {language === 'es' ? 'Nombre Completo' : 'Full Name'} *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={language === 'es' ? 'Su nombre completo' : 'Your full name'}
                        required
                        className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                        data-testid="contact-name"
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Email & Phone */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-[#0F172A] mb-2">
                          {t('contact.emailAddress')} *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="email@example.com"
                          required
                          className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                          data-testid="contact-email"
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0F172A] mb-2">
                          {t('contact.phoneNumber')} *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          required
                          className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
                          data-testid="contact-phone"
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                      </div>
                    </div>

                    {/* Service & ZIP in a row on desktop */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-[#0F172A] mb-2">
                          {language === 'es' ? 'Servicio Necesitado' : 'Service Needed'} *
                        </label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          required
                          className={`form-select ${errors.service ? 'border-red-500' : ''}`}
                          data-testid="contact-service"
                        >
                          <option value="">{t('contact.selectService')}</option>
                          {services.map((service) => (
                            <option 
                              key={service.value} 
                              value={service.value}
                            >
                              {service.label}
                            </option>
                          ))}
                        </select>
                        {errors.service && <p className="text-red-500 text-xs mt-1">{errors.service}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0F172A] mb-2">
                          {language === 'es' ? 'Código Postal' : 'ZIP Code'} *
                        </label>
                        <Input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleChange}
                          placeholder="90001"
                          required
                          maxLength="10"
                          className={`form-input ${errors.zipCode ? 'border-red-500' : ''}`}
                          data-testid="contact-zipcode"
                        />
                        {errors.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        {t('contact.yourMessage')}
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('contact.messagePlaceholder')}
                        rows={4}
                        className={`form-input resize-none ${errors.message ? 'border-red-500' : ''}`}
                        data-testid="contact-message"
                      />
                      {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                    </div>

                    {/* CAPTCHA */}
                    <div className="bg-[#F8FAFC] rounded-xl p-5 border border-[#E2E8F0]">
                      <label className="flex items-center gap-2 text-sm font-medium text-[#0F172A] mb-3">
                        <Shield size={16} className="text-[#1E88E5]" />
                        {language === 'es' ? 'Verificación de Seguridad' : 'Security Verification'} *
                      </label>
                      <div className="flex items-center gap-4">
                        <div className="text-xl font-bold text-[#1E88E5] bg-white px-5 py-2.5 rounded-lg border border-[#E2E8F0] whitespace-nowrap tracking-wide">
                          {captcha.num1} + {captcha.num2} = ?
                        </div>
                        <Input
                          type="number"
                          name="captchaAnswer"
                          value={formData.captchaAnswer}
                          onChange={handleChange}
                          placeholder="?"
                          required
                          className={`form-input w-20 text-center text-lg font-semibold ${errors.captchaAnswer ? 'border-red-500' : ''}`}
                          data-testid="contact-captcha"
                        />
                        <button 
                          type="button" 
                          onClick={() => { generateCaptcha(); setFormData(prev => ({ ...prev, captchaAnswer: '' })); }}
                          className="text-[#1E88E5] hover:text-[#1565C0] text-sm font-medium underline underline-offset-2 whitespace-nowrap"
                          data-testid="captcha-refresh"
                        >
                          {language === 'es' ? 'Cambiar' : 'Refresh'}
                        </button>
                      </div>
                      {errors.captchaAnswer && <p className="text-red-500 text-xs mt-2">{errors.captchaAnswer}</p>}
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary py-6 text-lg font-semibold tracking-wide"
                      data-testid="contact-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={20} className="mr-2 animate-spin" />
                          {language === 'es' ? 'Enviando...' : 'Sending...'}
                        </>
                      ) : (
                        <>
                          <Send size={20} className="mr-2" />
                          {language === 'es' ? 'Solicitar Estimado Gratis' : 'Get a Free On-Site Estimate'}
                        </>
                      )}
                    </Button>

                    {/* Privacy Note */}
                    <p className="text-xs text-[#94A3B8] text-center flex items-center justify-center gap-1.5">
                      <Shield size={12} />
                      {language === 'es' 
                        ? 'Su información está segura y nunca será compartida.' 
                        : 'Your information is secure and will never be shared.'}
                    </p>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="animate-fadeInUp animation-delay-200">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#E2E8F0] h-full lg:h-auto">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('contact.getInTouch')} <span className="text-[#1E88E5]">{t('contact.touchHighlight')}</span>
                </h2>

                <div className="space-y-4 mb-8">
                <a 
                  href="tel:+13233780061"
                  className="flex items-start gap-4 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] hover:border-[#1E88E5] transition-colors group"
                  data-testid="contact-phone-link"
                >
                  <div className="icon-container icon-container-blue">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] group-hover:text-[#1E88E5] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('contact.phone')}
                    </h3>
                    <p className="text-[#334155]">(323) 378-0061</p>
                    <p className="text-sm text-[#4CAF50] font-medium">{t('contact.clickToCall')}</p>
                  </div>
                </a>

                <a 
                  href="mailto:gelvia26@gmail.com"
                  className="flex items-start gap-4 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] hover:border-[#1E88E5] transition-colors group"
                  data-testid="contact-email-link"
                >
                  <div className="icon-container icon-container-green">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] group-hover:text-[#1E88E5] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('contact.email')}
                    </h3>
                    <p className="text-[#334155] break-all">gelvia26@gmail.com</p>
                    <p className="text-sm text-[#4CAF50] font-medium">{t('contact.clickToEmail')}</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                  <div className="icon-container icon-container-blue">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('contact.address')}
                    </h3>
                    <p className="text-[#334155]">Sylmar, CA 91342</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                  <div className="icon-container icon-container-green">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('contact.businessHours')}
                    </h3>
                    <p className="text-[#334155]">{t('contact.monSat')}</p>
                    <p className="text-[#4CAF50] text-sm font-medium">Emergency cleaning available</p>
                  </div>
                </div>
              </div>

              {/* Service Areas */}
              <div className="bg-[#F8FAFC] rounded-xl p-5 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('contact.serviceAreasTitle')}
                </h3>
                <p className="text-[#334155] text-sm mb-3">
                  {t('contact.serviceAreasText')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-[#334155] text-sm">
                    <CheckCircle2 size={14} className="text-[#4CAF50]" />
                    {language === 'es' ? 'Condado de Los Ángeles' : 'Los Angeles County'}
                  </li>
                  <li className="flex items-center gap-2 text-[#334155] text-sm">
                    <CheckCircle2 size={14} className="text-[#4CAF50]" />
                    {language === 'es' ? 'Valle de San Fernando' : 'San Fernando Valley'}
                  </li>
                  <li className="flex items-center gap-2 text-[#334155] text-sm">
                    <CheckCircle2 size={14} className="text-[#4CAF50]" />
                    {language === 'es' ? 'Sylmar y Áreas Cercanas' : 'Sylmar & Surrounding Areas'}
                  </li>
                </ul>
                <p className="text-xs text-[#64748B] mt-3">
                  {t('contact.notSure')}
                </p>
              </div>

              {/* Google Maps Embed */}
              <div className="bg-[#F8FAFC] rounded-xl overflow-hidden border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold p-4 border-b border-[#E2E8F0]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('contact.findUs')}
                </h3>
                <div className="aspect-video">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3296.8799392542784!2d-118.41499492357383!3d34.32376837304821!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c28fd6c0f8b8b1%3A0x8c8b8b8b8b8b8b8b!2s10420%20Arnwood%20Rd%2C%20Sylmar%2C%20CA%2091342!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Gomez Cleaning Service Location"
                    data-testid="google-map"
                  ></iframe>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
